import { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useFloating, offset, flip, shift, autoUpdate } from '@floating-ui/react';
import { FunnelIcon } from '@heroicons/react/24/outline';
import { FunnelIcon as FunnelIconSolid } from '@heroicons/react/24/solid';

const TableFilter = ({ title, options = [], selectedValues = [], onFilterChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const { refs, floatingStyles } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset(4), flip(), shift()],
    whileElementsMounted: autoUpdate,
    placement: 'bottom-start',
    strategy: 'fixed',
  });

  const setReferenceRef = useCallback(
    node => {
      refs.setReference(node);
    },
    [refs],
  );

  const setFloatingRef = useCallback(
    node => {
      refs.setFloating(node);
    },
    [refs],
  );

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    return options.filter(option => String(option).toLowerCase().includes(searchTerm.toLowerCase()));
  }, [options, searchTerm]);

  const handleToggle = value => {
    const newSelected = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];
    onFilterChange(newSelected);
  };

  const hasActiveFilters = selectedValues.length > 0;

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-medium">{title}</span>
      <div className="relative">
        <button
          ref={setReferenceRef}
          onClick={() => setIsOpen(!isOpen)}
          className={`btn btn-ghost btn-xs btn-square ${hasActiveFilters ? 'text-primary' : 'text-gray-400'}`}
        >
          {hasActiveFilters ? <FunnelIconSolid className="w-4 h-4" /> : <FunnelIcon className="w-4 h-4" />}
        </button>

        {isOpen && (
          <>
            <div className="fixed inset-0 z-[99]" onClick={() => setIsOpen(false)} />
            <div
              ref={setFloatingRef}
              style={floatingStyles}
              className="z-[100] bg-white shadow-lg rounded-lg border border-gray-200 w-64 p-2 flex flex-col gap-2"
            >
              <input
                type="text"
                placeholder="Filter..."
                className="input input-sm input-bordered w-full"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                autoFocus
              />

              <div className="max-h-48 overflow-y-auto space-y-1">
                {filteredOptions.length > 0 ? (
                  filteredOptions.map(option => (
                    <label key={option} className="flex items-center gap-2 p-1 hover:bg-gray-50 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        className="checkbox checkbox-xs checkbox-primary"
                        checked={selectedValues.includes(option)}
                        onChange={() => handleToggle(option)}
                      />
                      <span className="text-xs truncate" title={option}>
                        {option}
                      </span>
                    </label>
                  ))
                ) : (
                  <div className="text-xs text-gray-400 text-center py-2">No options found</div>
                )}
              </div>

              {hasActiveFilters && (
                <div className="border-t pt-2 mt-1">
                  <button
                    className="btn btn-xs btn-ghost w-full text-error"
                    onClick={() => {
                      onFilterChange([]);
                      setIsOpen(false);
                    }}
                  >
                    Clear Filters
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

TableFilter.propTypes = {
  title: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.string),
  selectedValues: PropTypes.arrayOf(PropTypes.string),
  onFilterChange: PropTypes.func.isRequired,
};

export default TableFilter;
